import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Section from '../components/layout/Section';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import FormInput from '../components/ui/FormInput';
import FileUpload from '../components/ui/FileUpload'; // Import FileUpload
import { useRealtorContext } from '../context/RealtorContext';
import toast from 'react-hot-toast';
import { UserCircleIcon, AcademicCapIcon, SparklesIcon } from '@heroicons/react/24/solid'; // Import SVG icons

const AffiliateRegisterPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { addRealtor, loading, error, currentUser } = useRealtorContext();
  const [referredById, setReferredById] = useState<string | null>(null);
  const [referrerName, setReferrerName] = useState<string | null>(null);
  const [referrerImage, setReferrerImage] = useState<string | null>(null); // New state for referrer image
  const [profileImage, setProfileImage] = useState<File | null>(null); // State for uploaded image
  const [selectedSvg, setSelectedSvg] = useState<string | null>(null); // State for selected SVG

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referredBy = params.get('referredBy');
    if (referredBy) {
      setReferredById(referredBy);
      // Fetch referrer's name and image from the backend
      const fetchReferrer = async () => {
        try {
          const response = await fetch(`https://uniquestatebackend.gomsolutions.com.ng/api/users/public/${referredBy}`);
          if (response.ok) {
            const data = await response.json();
            setReferrerName(data.name);
            setReferrerImage(data.image ? `http://localhost:5000${data.image}` : null);
          } else {
            console.error('Failed to fetch referrer details');
            setReferrerName(`Realtor ${referredBy}`); // Fallback
          }
        } catch (err) {
          console.error('Error fetching referrer:', err);
          setReferrerName(`Realtor ${referredBy}`); // Fallback
        }
      };
      fetchReferrer();
    }
  }, [location.search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = (file: File | null) => {
    setProfileImage(file);
    setSelectedSvg(null); // Clear SVG selection if image is uploaded
  };

  const handleSvgSelect = (svgName: string) => {
    setSelectedSvg(svgName);
    setProfileImage(null); // Clear image selection if SVG is selected
  };

  const getSvgComponent = (svgName: string) => {
    switch (svgName) {
      case 'UserCircleIcon': return <UserCircleIcon className="w-16 h-16 text-neutral-400" />;
      case 'AcademicCapIcon': return <AcademicCapIcon className="w-16 h-16 text-neutral-400" />;
      case 'SparklesIcon': return <SparklesIcon className="w-16 h-16 text-neutral-400" />;
      default: return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    let imageToUpload: File | null = null;
    if (profileImage) {
      imageToUpload = profileImage;
    } else if (selectedSvg) {
      // Convert SVG to a Blob or File if needed for backend upload
      // For now, we'll pass the SVG name as a string if the backend can handle it
      // or convert it to a data URL. For simplicity, let's assume backend can handle a string for SVG.
      // If backend expects a file, you'd need to convert the SVG to a Blob/File here.
      // For this implementation, we'll just pass the selectedSvg name as a string in the image field.
      // This might require backend adjustment to store SVG names or convert them to actual images.
      // For now, we'll treat it as a placeholder for the image field.
      // A more robust solution would involve client-side SVG to PNG/JPEG conversion.
    }

    try {
      await addRealtor(
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          role: 'realtor', // New registrations via affiliate link are for realtors
          joinDate: new Date().toISOString().split('T')[0],
          password: formData.password,
          commissionHistory: [], // Initialize commissionHistory
        },
        imageToUpload, // Pass the uploaded file
        referredById // Pass the referrer ID
      );
      toast.success('Registration successful! You can now log in.');
      navigate('/login');
    } catch (err: any) {
      toast.error(err.message || 'Registration failed. Please try again.');
    }
  };

  if (currentUser) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-neutral-50 via-white to-primary-50/30">
        <Navbar />
        <main className="flex-grow pt-20 flex items-center justify-center">
          <Card className="p-8 text-center">
            <h2 className="text-2xl font-bold text-primary-800 mb-4">Already Logged In</h2>
            <p className="text-neutral-700">You are already logged in. Redirecting to dashboard...</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-6">Go to Dashboard</Button>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-neutral-50 via-white to-primary-50/30">
      <Navbar />
      
      <main className="flex-grow pt-20">
        <Section className="py-20">
          <div className="max-w-2xl mx-auto">
            <Card className="p-8 lg:p-12" shadow="large" glass>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-display font-bold text-primary-800 mb-2">
                  Join Our Affiliate Network
                </h2>
                {referredById && referrerName && (
                  <p className="text-lg text-neutral-600 flex items-center justify-center gap-2">
                    You've been invited by <span className="font-semibold text-primary-700">{referrerName}</span>!
                    {referrerImage && (
                      <img src={referrerImage} alt={referrerName} className="w-8 h-8 rounded-full object-cover" />
                    )}
                  </p>
                )}
                <p className="text-md text-neutral-500 mt-2">
                  Register below to become a realtor and start earning commissions.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <FormInput
                  label="Full Name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <FormInput
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <FormInput
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />

                {/* Photo Upload or SVG Selection */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-neutral-700">Profile Photo</label>
                  <FileUpload onFileSelect={handleImageUpload} />
                  <p className="text-center text-neutral-500">OR select an avatar:</p>
                  <div className="flex justify-center space-x-4">
                    <div
                      className={`p-3 rounded-full cursor-pointer transition-all duration-200 ${selectedSvg === 'UserCircleIcon' ? 'ring-2 ring-primary-500 bg-primary-100' : 'hover:bg-neutral-100'}`}
                      onClick={() => handleSvgSelect('UserCircleIcon')}
                    >
                      <UserCircleIcon className="w-12 h-12 text-neutral-600" />
                    </div>
                    <div
                      className={`p-3 rounded-full cursor-pointer transition-all duration-200 ${selectedSvg === 'AcademicCapIcon' ? 'ring-2 ring-primary-500 bg-primary-100' : 'hover:bg-neutral-100'}`}
                      onClick={() => handleSvgSelect('AcademicCapIcon')}
                    >
                      <AcademicCapIcon className="w-12 h-12 text-neutral-600" />
                    </div>
                    <div
                      className={`p-3 rounded-full cursor-pointer transition-all duration-200 ${selectedSvg === 'SparklesIcon' ? 'ring-2 ring-primary-500 bg-primary-100' : 'hover:bg-neutral-100'}`}
                      onClick={() => handleSvgSelect('SparklesIcon')}
                    >
                      <SparklesIcon className="w-12 h-12 text-neutral-600" />
                    </div>
                  </div>
                  {selectedSvg && (
                    <div className="text-center mt-2">
                      <p className="text-sm text-neutral-600">Selected Avatar:</p>
                      {getSvgComponent(selectedSvg)}
                    </div>
                  )}
                </div>

                <FormInput
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <FormInput
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={loading}
                >
                  {loading ? 'Registering...' : 'Register as Realtor'}
                </Button>
              </form>
              <p className="text-center text-neutral-600 mt-6">
                Already have an account?{' '}
                <a href="/login" className="text-primary-600 hover:underline font-medium">
                  Log In
                </a>
              </p>
            </Card>
          </div>
        </Section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AffiliateRegisterPage;